import {sleep} from '@benchling/js-utils';
import {useCallback, useEffect, useState} from 'react';
import isEqual from 'react-fast-compare';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {makeNewEmptyMethodNode, normalizeNameForUniqueness} from 'procedures/design/flowchartManagementUtils';
import {getMethodNamePlaceholder} from 'procedures/design/procedureMethodNodeCommonConsts';
import {MethodNode, ProcedureFlowchart} from 'procedures/ProcedureDomainTypes';
import {omit} from 'util/omitKey';

function isMethodNodeEmpty(methodNode: MethodNode, procedureType: ProcedureType): boolean {
  const omitKeys: Array<keyof MethodNode> = ['id', 'cid', 'nodeId', 'provenanceId', 'canvasGridCoordinates'];
  return (
    isEqual(
      omit(methodNode, ...omitKeys),
      omit(makeNewEmptyMethodNode(ProcedureType.RECIPE, 0, 0, 'cid'), ...omitKeys)
    ) &&
    (methodNode.name.length === 0 ||
      methodNode.name === normalizeNameForUniqueness('', [], getMethodNamePlaceholder(procedureType)))
  );
}

function isInitialFlowchart(flowchart: ProcedureFlowchart, procedureType: ProcedureType): boolean {
  const hasOneNode = flowchart.nodes.length === 1;
  const hasNoEdges = flowchart.edges.length === 0;
  return hasOneNode && hasNoEdges && isMethodNodeEmpty(flowchart.nodes[0], procedureType);
}

export interface EditingMethodNameState {
  editingNameMethodCid: string | null;
  // Use this if you the node has already rendered before
  setEditingNameMethodCid: (methodCid: string | null) => void;
  // Use this if you are mounting the node for the first time
  autofocusEditNameOnNewMethod: (newMethodCid: string) => void;
}

/**
 * This hook creates a state to keep track of which method's name is being edited. This is specifically
 * relevant when a new method node is added and the focus needs to be on the name input field after first
 * render cycle is completed to render the node.
 */
export default function useEditingMethodNameState(
  flowchart: ProcedureFlowchart,
  procedureType: ProcedureType
): EditingMethodNameState {
  const [editingNameMethodCid, setEditingNameMethodCid] = useState<string | null>(null);

  /**
   * The first time a reactflow node is mounted, reactflow only places it in the canvas after the first render
   * cycle is completed. This means that any focus-on-first-render code will no-op since there's nothing to
   * focus on yet.
   *
   * To ensure that newly mounted method nodes autofocus their edit-name input properly, defer opening the
   * edit-name input to the next JS event loop tick. This guarantees every method node is placed properly
   * into the DOM canvas and can handle focus events.
   */
  const deferredSetEditingName = useCallback(async (newMethodCid: string) => {
    // State is saved in Redux. In this situation, the relevant state change requires two separate redux actions,
    // so we defer twice here.
    await sleep(1);
    await sleep(1);
    setEditingNameMethodCid(newMethodCid);
  }, []);

  useEffect(() => {
    if (isInitialFlowchart(flowchart, procedureType)) {
      deferredSetEditingName(flowchart.nodes[0].cid);
    }
    // Only run once on first mount to fix autofocus on initial method name editor input
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    editingNameMethodCid,
    setEditingNameMethodCid,
    autofocusEditNameOnNewMethod: deferredSetEditingName,
  };
}
